import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
} from "@mui/material";
import { Text } from "@src/lib/ui-react";
import CloseIcon from "@mui/icons-material/Close";
import { useModalState, UseModalState } from "@src/lib/ui-react";
import { FormProvider, useForm, UseFormReturn } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback, useRef } from "react";
import { isDefined } from "@clipboard-health/util-ts";

interface EditContentForm {
  content: string;
}

export type UseEditContentDialogState = {
  openModal: (content?: string) => Promise<string | undefined>;
  onSave: (content: string) => void;

  modalState: UseModalState;
  form: UseFormReturn<EditContentForm>;
  fieldName: string;
  minCharacters: number;
  maxCharacters: number;
};

export interface UseEditContentDialogStateOptions {
  fieldName: string;
  minCharacters: number;
  maxCharacters: number;
}

export function useEditContentDialogState(
  options: UseEditContentDialogStateOptions
): UseEditContentDialogState {
  const { fieldName, minCharacters, maxCharacters } = options;

  const modalState = useModalState();
  const resolveOpenModalPromise = useRef<(content: string) => void>();

  const formSchema = z.object({
    content: z
      .string({
        required_error: `${fieldName} must contain at least ${minCharacters} characters`,
        invalid_type_error: `${fieldName} must contain at least ${minCharacters} characters`,
      })
      .min(minCharacters, `${fieldName} must contain at least ${minCharacters} characters`)
      .max(maxCharacters, `${fieldName} must contain at most ${maxCharacters} characters`),
  });

  const form = useForm<EditContentForm>({
    defaultValues: { content: "" },
    resolver: zodResolver(formSchema),
  });

  const openModal = useCallback(
    async (content?: string) => {
      form.setValue("content", content ?? "");
      modalState.openModal();

      return new Promise<string>((resolve) => {
        resolveOpenModalPromise.current = resolve;
      });
    },
    [modalState, form]
  );

  const onSave = useCallback(
    (content: string) => {
      resolveOpenModalPromise.current?.(content);
      modalState.closeModal();
    },
    [modalState]
  );

  return {
    modalState,
    openModal,
    onSave,
    form,
    fieldName,
    minCharacters,
    maxCharacters,
  };
}

export interface EditContentDialogProps {
  dialogState: UseEditContentDialogState;
  dialogTitle: string;
}

export function EditContentDialog(props: EditContentDialogProps): JSX.Element {
  const {
    dialogState: { modalState, form, fieldName, maxCharacters, onSave },
    dialogTitle,
  } = props;

  const {
    watch,
    register,
    handleSubmit,
    formState: { errors },
  } = form;
  const input = register("content");
  const value = watch("content");
  const hasError = isDefined(errors.content?.message);

  return (
    <Dialog
      onClose={() => modalState.closeModal()}
      open={modalState.modalIsOpen}
      fullWidth
      maxWidth="md"
    >
      <FormProvider {...form}>
        <form onSubmit={handleSubmit((params) => onSave(params.content))}>
          <DialogTitle sx={{ paddingRight: 4 }}>{dialogTitle}</DialogTitle>
          <IconButton
            sx={{ position: "absolute", right: 1.5, top: 1.5 }}
            size="large"
            color="inherit"
            onClick={() => modalState.closeModal()}
          >
            <CloseIcon />
          </IconButton>
          <DialogContent dividers>
            <TextField
              {...input}
              label={`${fieldName} (this information will be shared with the professional)`}
              placeholder={`Type the ${fieldName}...`}
              fullWidth
              multiline
              rows={8}
              error={hasError}
              helperText={
                <Stack justifyContent="space-between" direction="row">
                  <Text variant="body2" color="inherit">
                    {errors.content?.message ?? " "}
                  </Text>
                  <Text variant="body2" color="inherit">
                    {value?.length ?? 0} / {maxCharacters}
                  </Text>
                </Stack>
              }
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => modalState.closeModal()}>Cancel</Button>
            <Button variant="contained" type="submit">
              Save
            </Button>
          </DialogActions>
        </form>
      </FormProvider>
    </Dialog>
  );
}
